import { BackstageOverrides } from '@backstage/core-components';
import { BackstageOverrides as CatalogReactOverrides } from '@backstage/plugin-catalog-react';
import {
  createTheme,
  createUnifiedThemeFromV4,
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  UnifiedTheme,
} from '@backstage/theme';

import { alpha, Theme } from '@material-ui/core/styles';
import { AutocompleteClassKey } from '@material-ui/lab/Autocomplete';
import { AlertClassKey } from '@material-ui/lab/Alert';

import './fonts.css'


const textColor = "#211710"


export const PandoraPink = "#FF93A0";
export const PandoraBlack = "#211710";
export const PandoraGrey = "#C5C5C5";
export const PandoraRed = "#C91D41";
export const PandoraOrange = "#E5AC52";
export const PandoraYellow = "#ECD874";
export const PandoraGreen = "#A7CB72";
export const PandoraBlue = "#6B8FD1";
export const PandoraPurple = "#B787D4";


// Labs types not included in overrides; https://github.com/mui/material-ui/issues/19427
declare module '@material-ui/core/styles/overrides' {
  export interface ComponentNameToClassKey {
    MuiAlert: AlertClassKey;
    MuiAutocomplete: AutocompleteClassKey;
  }
}

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: textColor,
  };
});

// TODO(awanlin): Continuing to use the deprecated `createTheme` for now
// will come back to clean this up when we have a better solution for this

// eslint-disable-next-line
const baseTheme = createTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: textColor,
        light: '#4C9AFF',
        dark: textColor,
      },
      secondary: {
        main: '#FF5630',
        light: '#FFAB00',
        dark: '#6554C0',
      },
      grey: {
        50: '#C1C7D0',
        100: '#7A869A',
        200: '#6B778C',
        300: '#5E6C84',
        400: '#505F79',
        500: '#42526E',
        600: '#344563',
        700: '#253858',
        800: '#172B4D',
        900: '#091E42',
      },
      error: {
        main: '#FF5630',
        light: '#FF8F73',
        dark: '#DE350B',
      },
      warning: {
        main: '#FFAB00',
        light: '#FFE380',
        dark: '#FF8B00',
      },
      success: {
        main: '#36B37E',
        light: '#79F2C0',
        dark: '#006644',
      },
      info: {
        main: '#0065FF',
        light: '#4C9AFF',
        dark: '#0747A6',
      },
      navigation: {
        ...palettes.light.navigation,
        background: PandoraPink,
        color: textColor,
        indicator: '#2684FF',
        navItem: {
          hoverBackground: 'rgba(116,118,121,0.6)',
        },
      },
      text: {
        primary: textColor,
      },
      background: {
        default: '#FFFFFF',
      },
    },
  }),
  fontFamily: 'GothamSSmMedium',
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  typography: {
    fontFamily: 'GothamSSmMedium',
    htmlFontSize: 14,
    h1: {
      fontSize: 40,
      fontWeight: 500,
      marginBottom: 4
    },
    h2: {
      fontSize: 28,
      fontWeight: 500,
      marginBottom: 4
    },
    h3: {
      fontSize: 20,
      fontWeight: 500,
      marginBottom: 4
    },
    h4: {
      fontSize: 20,
      fontWeight: 500,
      marginBottom: 4
    },
    h5: {
      fontSize: 18,
      fontWeight: 500,
      marginBottom: 4
    },
    h6: {
      fontSize: 16,
      fontWeight: 500,
      marginBottom: 4
    },
  }
});

const createCustomThemeOverrides = (
  theme: Theme,
): BackstageOverrides & CatalogReactOverrides => {
  return {
    BackstageHeader: {
      header: {
        backgroundImage: 'unset',
        boxShadow: 'unset',
        paddingBottom: theme.spacing(1),
        backgroundColor: "#FFF"
      },
      title: {
        color: textColor,
        fontWeight: 700,
        fontSize: "40",
        textTransform: "uppercase",
      },
      subtitle: {
        color: textColor,
        fontWeight: 400,
        fontSize: "20",
        textTransform: "uppercase",
      },
      type: {
        color: alpha(theme.page.fontColor, 0.8),
      },

    },
    MuiChip: {
      root: {
        borderRadius: 3,
        color: textColor,
        margin: 4,
        backgroundColor: PandoraPink,
      },
      outlined: {
        backgroundColor: PandoraPink,
      }
    },
    MuiMenuItem: {
      root: {
        fontFamily: "GothamSSmMedium",
        minHeight: 42,
      }
    },
    MuiAppBar: {
      colorDefault: {
        backgroundColor: '#FFFFFF',
        color: textColor,
      },
      root: {
        boxShadow: '0 4px 18px rgba(33,23,16,0.06)',
      },
    },
    MuiToolbar: {
      regular: {
        minHeight: 72,
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        minWidth: 72,
        fontFamily: 'GothamSSmMedium',
        fontWeight: 500,
        fontSize: 14,
      },
      textColorPrimary: {
        color: alpha(textColor, 0.72),
        '&$selected': {
          color: textColor,
        },
      },
    },
    MuiTabs: {
      indicator: {
        height: 3,
        borderRadius: 3,
        backgroundColor: PandoraPink,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 8,
      },
    },
    MuiCard: {
      root: {
        backgroundColor: '#FFFFFF',
        border: '1px solid #E7DED6',
        boxShadow: '0 2px 10px rgba(33,23,16,0.05)'
      }
    },
    MuiButton: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
      },
      contained: {
        boxShadow: 'none',
      },
    },
    MuiLink: {
      root: {
        position: 'relative',
        display: 'inline-block',
        color: textColor,
        textDecoration: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          bottom: -2,
          width: '100%',
          height: 2,
          backgroundColor: PandoraPink,
          transform: 'scaleX(0)',
          transformOrigin: 'left center',
          transition: 'transform 240ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover::after, &:focus-visible::after': {
          transform: 'scaleX(1)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&::after': {
            transition: 'none',
          },
        },
      },
      underlineNone: {
        textDecoration: 'none',
      },
      underlineHover: {
        '&:hover': {
          textDecoration: 'none',
        },
        textDecoration: 'none',
      },
      underlineAlways: {
        textDecoration: 'none',
      },
    },
    MuiBreadcrumbs: {
      root: {
        '& span': {
          textDecoration: 'none !important',
          textTransform: 'none !important',
          textUnderlineOffset: '1px !important',
        },
        '& div': {
          textTransform: 'none !important',
        }
      },
      separator:{
        color: PandoraPink
      }
    },
    MuiTypography: {
      root: { fontFamily: "GothamSSmMedium", fontSize: 14, fontWeight: 400, fontSynthesis: "none" },
      h1: { fontFamily: "GothamSSmMedium", fontSize: 40, fontWeight: 500 },
      h2: { fontFamily: "GothamSSmMedium", fontSize: 28, fontWeight: 500 },
      h3: { fontFamily: "GothamSSmMedium", fontSize: 20, fontWeight: 500 },
      h4: { fontFamily: "GothamSSmMedium", fontSize: 20, fontWeight: 500 },
      h5: { fontFamily: "GothamSSmMedium", fontSize: 11, fontWeight: 500 },
      h6: { fontFamily: "GothamSSmMedium", fontSize: 11, fontWeight: 500 },
      body1: { fontFamily: "GothamSSmBook", fontSize: 16, fontWeight: 400, letterSpacing: "0em", lineHeight: "24px" },
      body2: { fontFamily: "GothamSSmBook", fontSize: 13, fontWeight: 400, letterSpacing: "0em", lineHeight: "20px" },
      subtitle1: { fontFamily: "GothamSSmBook", fontSize: 20, fontWeight: 500 },
      subtitle2: { fontFamily: "GothamSSmBook", fontSize: 16, fontWeight: 500 },
    },
    MuiCardHeader: {
      root: {
        backgroundColor: '#F8F3EE',
        borderBottom: '1px solid #E7DED6',
        paddingTop: 14,
        paddingBottom: 14,
      },
      title: {
        fontSize: 20,
        fontWeight: 500,
        textTransform: 'none',
      },
      action:{
        '& button, & a': {
          paddingTop: '8px',
          paddingBottom: '8px',
        }
      }
    },
    MuiCardContent: {
      root: {
        backgroundColor: '#FFFFFF',
      },
    },

    BackstageItemCardHeader: {
      root: {
        backgroundImage: "",
        backgroundColor: '#F8F3EE',
        borderBottom: '1px solid #E7DED6',
        color: textColor
      }
    },

    BackstageTableToolbar: {
      root: {

        // Many Backstage cards with tables render a toolbar instead of MuiCardHeader.
        backgroundColor: '#F8F3EE',
        borderBottom: '1px solid #E7DED6',
        minHeight: 56,
      },
      title: {
        '& h2': {
          fontSize: 20,
          fontWeight: 500,
          textTransform: 'none',

        }

      }
    },
    BackstageTableFiltersContainer:{
      root:{
        backgroundColor: '#F8F3EE',
        borderBottom: '1px solid #E7DED6',
        minHeight: 56,
      }
    },
    BackstageTableHeader: {
      header: {
        fontSynthesis: "none",
      }
    },
    BackstageSidebarPage: {
      root: {
        // Top navigation no longer uses the legacy sidebar rail offset.
        paddingLeft: '0 !important',
        [theme.breakpoints.up('sm')]: {
          paddingLeft: '0 !important',
        },
      },
    },
    
  };
};

export const pandoraTheme: UnifiedTheme = createUnifiedThemeFromV4({
  ...baseTheme,
  overrides: {
    ...baseTheme.overrides,
    ...createCustomThemeOverrides(baseTheme),

  },
});

/*
:root {
    --blue: #27251f;
    --indigo: #6610f2;
    --purple: #6f42c1;
    --pink: #e83e8c;
    --red: #c00;
    --orange: #fd7e14;
    --yellow: #ffc107;
    --green: #008827;
    --teal: #20c997;
    --cyan: #17a2b8;
    --white: #fff;
    --gray: #6c757d;
    --gray-dark: #343a40;
    --primary: #ffcad4;
    --secondary: #27251f;
    --success: #00823a;
    --info: #1979ff;
    --warning: #f79e13;
    --danger: #d91f46;
    --light: #f8f9fa;
    --dark: #343a40;
    --breakpoint-xs: 0;
    --breakpoint-sm: 544px;
    --breakpoint-md: 768px;
    --breakpoint-lg: 1025px;
    --breakpoint-xl: 1200px;
}
*/